/**
 * The `<PLANE>.DAT` gun block, transcribed for all 33 aircraft — the
 * variable-length half of the record that `aircraft-table.ts` could not carry
 * as one row per aircraft (a two-seater has two 16-byte gun positions, a
 * heavy bomber three).
 *
 * Same conventions as its sibling: **raw file values only**, every derivation
 * (metres, the model→body frame swap, radians) done in `aircraft-spec.ts`,
 * and the same standing-in-for-a-bake-stage note (AD-007e). The decoder these
 * numbers were read with is real code, not a scratch script —
 * `@red-opus/formats`' `decodePlaneDat` — and `tools/bake`'s test re-decodes
 * all 33 files and asserts this table against them, so a transcription slip
 * fails the suite rather than surviving review.
 *
 * ## What the three position fields are, and how that was settled
 *
 * `+0/+2/+4` are a point in the aircraft's own **`.TBL` model frame**: X span
 * (right positive), Y longitudinal (nose positive), Z up, at the models'
 * ≈65 units per metre, sharing the `.TBL` origin. Three independent checks
 * over all 44 gun records, none of which would survive a wrong unit or a
 * wrong origin:
 *
 * - **44/44** lie inside their own aircraft's baked `.TBL` bounding box
 *   inflated by 10% — across a fleet whose models span 375 to 1,609 units
 *   nose-to-tail, so a scale error of any size would show.
 * - Every one of the **26** tractor aircraft's fixed forward guns sits
 *   *behind* its own model's propeller-disc plane, by 0-31 units (0-48 cm),
 *   with the single exception of the Snipe at +19; and the two **pushers**
 *   (`AIRCO`, `FE2`) — whose propeller plane is *behind* the origin — put
 *   their nose guns 141 and 236 units ahead of it. The rule and its two
 *   exceptions are the same fact: a synchronised gun's muzzle is just short
 *   of the disc, and a pusher's is not.
 * - Every flexible gun lands on the crew bay its own model marks with a
 *   sphere node (the `.TBL` "pilot's head" primitive): `BRISTOL`'s observer
 *   gun at y = −234 against crew spheres at −141 and −90, and so on.
 *
 * `FE2`'s rear gun is the sharpest single case: `up = 173` is 5 units *above*
 * the model's own highest triangle, which is exactly where an F.E.2b's
 * telescopic Lewis on its rear mount sits — over the top wing. The Gotha's
 * and Handley Page's `up = −48 / −54` are the only negative heights in the
 * fleet, and both come paired with a **downward** traverse arc
 * (−8000…−2500 BAM = −44°…−13.7°): two independent fields describing the same
 * ventral tunnel gun. That an elevation arc *excludes zero* is also what rules
 * out the fields being a dispersion cone — see `combat/traverse.ts`.
 *
 * ## What the block does *not* contain
 *
 * There is no per-gun jam or effectiveness field — all 16 bytes are accounted
 * for by 3 position `int16`, the 2 mount bytes and 4 arc `int16`. Jam
 * (offset 208, 200 fleet-wide) and effectiveness (206) are per-*aircraft*.
 * And there is no twin-barrel separation: a twin mount is **one** record plus
 * the `TWIN_CENTRED` mount class, so how far apart the two barrels sit stays a
 * port choice (`gunnery-tunables.ts`'s `gunMuzzleOffsetM`).
 */

import type { AircraftKey } from "./aircraft-table.ts";

/**
 * One 16-byte gun position, in `.DAT` field order. A labelled tuple rather
 * than an object per record so all 44 stay reviewable as one table — the
 * labels are the column headings.
 */
export type GunBlockRow = readonly [
  /** `+0` — model-frame X, right positive. Zero on 39 of the 44 records. */
  rightUnits: number,
  /** `+2` — model-frame Y, nose positive. */
  forwardUnits: number,
  /** `+4` — model-frame Z, up positive. */
  upUnits: number,
  /** `+6` — mount class: 0 centred twin, 2 single left, 4 single right, 6 single centred. `+7` is always this plus one. */
  mountClass: number,
  /** `+8` — traverse arc, elevation from, BAM16 (`combat/traverse.ts`). */
  verticalFromBam: number,
  /** `+10` — elevation to. */
  verticalToBam: number,
  /** `+12` — azimuth from; greater than `+14` on a gunner's station, which wraps the arc through the rear. */
  horizontalFromBam: number,
  /** `+14` — azimuth to. */
  horizontalToBam: number,
];

/**
 * Gun positions in file order. On every multi-station aircraft, **station 0
 * is the flexible gun and the last station is the fixed forward one** — read
 * straight off the arcs: station 0 always carries a rear-hemisphere
 * horizontal arc, the last a symmetric fighter cone.
 */
export const AIRCRAFT_GUN_BLOCKS = {
  MORANE:    [[   0,   49,   30, 6,   -500,   500,   -500,    500]],
  FOKE3:     [[   0,   52,   34, 4,   -500,   500,   -500,    500]],
  AIRCO:     [[   0,  106,   43, 6,   -500,   500,   -500,    500]],
  NIEU11:    [[  20,   60,   35, 4,   -500,   500,   -500,    500]],
  NIEUPORT:  [[  35,   76,    0, 6,   -500,   500,   -500,    500]],
  SOPPUP:    [[  35,   76,    8, 6,   -500,   500,   -500,    500]],
  SPAD_7:    [[   0,   71,   24, 4,   -500,   500,   -500,    500]],
  ALBA_D2:   [[   0,   84,   40, 0,   -500,   500,   -500,    500]],
  SOPTRI:    [[   0,   65,   34, 4,   -500,   500,   -500,    500]],
  ALBA_D3:   [[   0,   84,   40, 0,   -500,   500,   -500,    500]],
  HALBD2:    [[ -20,   75,   35, 2,   -500,   500,   -500,    500]],
  SE5:       [[   0,   63,   34, 2,   -500,   500,   -500,    500]],
  ALBA_D5:   [[   0,   84,   40, 0,   -910,   910,   -910,    910]],
  CAMEL:     [[   0,   76,   22, 0,   -500,   500,   -500,    500]],
  PFALZ:     [[   0,   70,   43, 0,   -500,   500,   -500,    500]],
  FOKDR1:    [[   0,   33,    0, 0,   -500,   500,   -500,    500]],
  SPAD_13:   [[   0,   71,   24, 0,   -500,   500,   -500,    500]],
  NIEU28:    [[ -20,   75,   35, 2,   -500,   500,   -500,    500]],
  FOKD7:     [[   0,   79,   40, 0,   -500,   500,   -500,    500]],
  SIEMENS:   [[   0,   75,   30, 0,   -500,   500,   -500,    500]],
  SNIPE:     [[   0,   96,   26, 0,   -500,   500,   -500,    500]],
  FOKD8:     [[   0,   70,   35, 0,   -500,   500,   -500,    500]],
  BE2:       [[   0,  -96,   58, 6,   4000, 10000,  25000, -25000]],
  AVIATIK:   [[   0,   62,   32, 6,   4000, 10000,  25000, -25000]],
  FE2:       [[   0,  -85,  173, 6,   6000, 12000,  25000, -25000], [   0,  176,   71, 6,  -4000,  4000,  -8000,   8000]],
  ROLAND:    [[   0, -135,   59, 6,   3000,  9000,  24000, -24000], [   0,   73,   40, 6,   -500,   500,   -500,    500]],
  RE8:       [[   0, -184,   39, 0,   3000,  9000,  24000, -24000], [   0,   81,   39, 0,   -500,   500,   -500,    500]],
  RUMPLER:   [[   0, -194,   42, 6,   3000,  9000,  24000, -24000], [   0,   85,   20, 6,   -500,   500,   -500,    500]],
  BRISTOL:   [[   0, -234,   40, 0,   3000,  9000,  24000, -24000], [   0,  106,   50, 6,   -500,   500,   -500,    500]],
  DH4:       [[   0, -221,   39, 0,   3000,  9000,  24000, -24000], [   0,  100,   31, 6,   -500,   500,   -500,    500]],
  JUNKER:    [[   0, -129,   53, 6,   3000,  9000,  24000, -24000], [   0,   83,   35, 0,   -500,   500,   -500,    500]],
  GOTHA:     [[   0, -214,  115, 6,   2500,  8000,  24000, -24000], [   0, -236,  -48, 6,  -8000, -2500,  24000, -24000], [   0,  357,   84, 6,  -5000,  5000,  -4000,   4000]],
  HANDLEY:   [[   0, -319,  172, 0,   2500,  8000,  24000, -24000], [   0, -326,  -54, 6,  -8000, -2500,  24000, -24000], [   0,  526,  118, 0,  -5000,  5000,  -4000,   4000]],
} satisfies Record<AircraftKey, readonly GunBlockRow[]>;

/** `+6` mount classes. See `@red-opus/formats`' `GUN_MOUNT_CLASSES` for the cockpit-art evidence behind the names. */
export const GUN_MOUNT_TWIN_CENTRED = 0;
export const GUN_MOUNT_SINGLE_LEFT = 2;
export const GUN_MOUNT_SINGLE_RIGHT = 4;
export const GUN_MOUNT_SINGLE_CENTRED = 6;

/** Barrels a mount class fires from: 2 for the centred-twin class, 1 for the other three. */
export function barrelsForMountClass(mountClass: number): number {
  return mountClass === GUN_MOUNT_TWIN_CENTRED ? 2 : 1;
}

export function gunBlocksOf(key: AircraftKey): readonly GunBlockRow[] {
  return AIRCRAFT_GUN_BLOCKS[key];
}

/**
 * The station that fires down the boresight: the **last** one, which on every
 * multi-station aircraft is the fixed forward gun (the earlier stations carry
 * rear-hemisphere arcs). Single-station aircraft have only this one.
 */
export function forwardGunBlockOf(key: AircraftKey): GunBlockRow {
  const blocks = AIRCRAFT_GUN_BLOCKS[key];
  return blocks[blocks.length - 1]!;
}
